import type { ScriptShot, ScriptCharacter, ScriptProp, ScriptScene } from '@packages/global';

/**
 * 缩略图数据接口
 */
export interface ThumbnailData {
  /** 缩略图类型 */
  type: 'scene' | 'character' | 'prop';
  /** 元素名称 */
  name: string;
  /** 图片URL */
  imageUrl?: string;
  /** 描述信息 */
  description?: string;
  /** AI提示词 */
  prompt?: string;
}

/**
 * 查找分镜的缩略图数据
 * 
 * 通过名称匹配从场景、角色、道具数组中查找对应的缩略图数据
 * 返回顺序：场景 → 角色 → 道具
 * 
 * @param shot - 分镜对象
 * @param scenes - 场景数组
 * @param characters - 角色数组
 * @param props - 道具数组
 * @returns 缩略图数据数组
 * 
 * @example
 * ```ts
 * const thumbnails = findThumbnails(shot, scenes, characters, props);
 * // 返回: [{ type: 'scene', name: '森林', imageUrl: '...' }, ...]
 * ```
 */
export function findThumbnails(
  shot: ScriptShot,
  scenes: ScriptScene[],
  characters: ScriptCharacter[],
  props: ScriptProp[]
): ThumbnailData[] {
  const thumbnails: ThumbnailData[] = [];

  // 1. 查找场景
  if (shot.sceneName) {
    const scene = scenes.find(s => s.name === shot.sceneName);
    if (scene) {
      thumbnails.push({
        type: 'scene',
        name: scene.name,
        imageUrl: scene.imageUrl,
        description: scene.description,
        prompt: scene.prompt,
      });
    }
  }

  // 2. 查找角色
  if (shot.subjects && shot.subjects.length > 0) {
    for (const subjectName of shot.subjects) {
      const character = characters.find(c => c.name === subjectName);
      if (character) {
        thumbnails.push({
          type: 'character',
          name: character.name,
          imageUrl: character.imageUrl,
          description: character.description,
          prompt: character.prompt,
        });
      }
    }
  }

  // 3. 查找道具
  if (shot.props && shot.props.length > 0) {
    for (const propName of shot.props) {
      const prop = props.find(p => p.name === propName);
      if (prop) {
        thumbnails.push({
          type: 'prop',
          name: prop.name,
          imageUrl: prop.imageUrl,
          description: prop.description,
          prompt: prop.prompt,
        });
      }
    }
  }

  return thumbnails;
}

/**
 * 限制缩略图数量
 * 
 * 最多返回 5 个缩略图，超过部分会被截断
 * 
 * @param thumbnails - 缩略图数组
 * @param maxCount - 最大数量，默认为 5
 * @returns 限制后的缩略图数组和剩余数量
 * 
 * @example
 * ```ts
 * const { limited, remaining } = limitThumbnails(thumbnails);
 * // limited: 前5个缩略图
 * // remaining: 剩余数量（如果有的话）
 * ```
 */
export function limitThumbnails(
  thumbnails: ThumbnailData[],
  maxCount: number = 5
): { limited: ThumbnailData[]; remaining: number } {
  if (thumbnails.length <= maxCount) {
    return {
      limited: thumbnails,
      remaining: 0,
    };
  }

  return {
    limited: thumbnails.slice(0, maxCount),
    remaining: thumbnails.length - maxCount,
  };
}
